"use client"

import {
    type InfinitePageData,
    useSWRInfiniteProductListing
} from "@/app/(site)/_utils/_hooks/SWR/useSWRInfiniteProductListing"
import { useProductGridTracking } from "@/app/(site)/_utils/_hooks/useProductGridTracking"
import { useScrollRestore } from "@/app/(site)/_utils/_hooks/useScrollRestore"
import { type VariantSortPreference } from "@/app/(site)/_utils/seasonalMerchandising"
import {
    useProductGridContext,
    useStaticGridContext
} from "@/app/(site)/contexts/product-grid/ProductGridContext"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef } from "react"

const buildPageUrl = (
    pageNum: number,
    basePath: string,
    searchParams: URLSearchParams
) => {
    const p = new URLSearchParams(searchParams.toString())
    if (pageNum <= 1) {
        p.delete("page")
    } else {
        p.set("page", String(pageNum))
    }
    const qs = p.toString()
    return qs ? `${basePath}?${qs}` : basePath
}

type UseProductGridDataParams = {
    serverFetchedPages: InfinitePageData[]
    externalId: string | undefined
    params: { locale: string; categories: string[] }
    nHits: number
    marketCode: string
    localeCode: string
    depictSessionId: string
    fetchLimit: number
    sortPreference?: VariantSortPreference
    currentPage: number
    totalPages: number
    listHierarchy: string
}

export function useProductGridData({
    serverFetchedPages,
    externalId,
    params,
    nHits,
    marketCode,
    localeCode,
    depictSessionId,
    fetchLimit,
    sortPreference,
    currentPage,
    totalPages,
    listHierarchy
}: UseProductGridDataParams) {
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const siteContext = useSiteContext()
    const { preserveScroll } = useScrollRestore()

    const { filterState, hasFiltersOrSort } = useProductGridContext()
    const { facetMappings } = useStaticGridContext()

    const {
        products,
        visiblePageProducts,
        activeStartPage,
        hasMorePages,
        hasPreviousPages,
        size,
        isValidating: isLoadingMore,
        loadMoreSWR,
        loadPreviousSWR,
        syncToServerPage,
        clientNHits
    } = useSWRInfiniteProductListing(
        {
            externalId,
            marketCode,
            localeCode,
            depictSessionId,
            limit: fetchLimit,
            sortPreference,
            filters: filterState.filters,
            sort: filterState.sort
        },
        // Previously read `basketSession?.metadata?.currencyFormat`
        // from `useSWRBasketSessionState()`. Dropped because (a) browsing visitors don't
        // have a Centra session in the new model, and (b) `useSWRInfiniteProductListing`
        // already accepts `CurrencyFormat | undefined` and degrades gracefully.
        undefined,
        params.locale,
        serverFetchedPages,
        currentPage,
        totalPages,
        facetMappings.generalSizeToSizeMap
    )

    const pathnameWithoutLocale = useMemo(() => {
        const segments = pathname.split("/")
        return "/" + segments.slice(2).join("/")
    }, [pathname])

    const prevPageUrl = hasPreviousPages
        ? buildPageUrl(activeStartPage - 1, pathnameWithoutLocale, searchParams)
        : null
    const nextPageUrl = hasMorePages
        ? buildPageUrl(size + 1, pathnameWithoutLocale, searchParams)
        : null
    const effectiveNHits = clientNHits ?? nHits
    const displayStart = (activeStartPage - 1) * fetchLimit + 1
    const displayEnd = Math.min(displayStart + products.length - 1, effectiveNHits)
    const amountOfSkeletons = fetchLimit - 8
    const skeletonArray = Array.from({ length: amountOfSkeletons })
    const prevCurrentPage = useRef(currentPage)

    useEffect(() => {
        if (prevCurrentPage.current !== currentPage) {
            prevCurrentPage.current = currentPage
            syncToServerPage(currentPage, serverFetchedPages)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage])

    const loadMore = async () => {
        await preserveScroll(async () => {
            const newSize = loadMoreSWR()
            if (newSize === null) return
            window.history.pushState(
                null,
                "",
                buildPageUrl(newSize, pathname, searchParams)
            )
        })
    }

    const loadPrevious = async () => {
        let newStart: number | null = null
        await preserveScroll(async () => {
            newStart = await loadPreviousSWR()
        })
        if (newStart === null) return
        router.push(buildPageUrl(newStart, pathname, searchParams), { scroll: false })
    }

    useProductGridTracking({
        products,
        filterState,
        isLoading: isLoadingMore,
        externalId,
        categoryPath: params.categories.join("/"),
        listHierarchy,
        siteContext,
        locale: params.locale
    })

    return {
        products,
        visiblePageProducts,
        activeStartPage,
        hasFiltersOrSort,
        effectiveNHits,
        displayStart,
        displayEnd,
        hasPreviousPages,
        hasMorePages,
        isLoadingMore,
        prevPageUrl,
        nextPageUrl,
        skeletonArray,
        size,
        serverFetchedPages,
        loadMore,
        loadPrevious
    }
}
